// src/components/tutor/mobile/MobileTutorMyInfo.js
import React, { useEffect, useState, useCallback } from "react";
import { auth } from "../../../firebaseConfig";
import { fetchMyTutorDoc, updateMyTutorDoc } from "../tutorFirestore";
import { normalizeStatus } from "../tutorStyles";
import "./MobileMyInfo.css";

function formatDateDisplay(str) {
  if (!str) return "—";
  const [y, m, d] = str.split("-").map(Number);
  if (!y || !m || !d) return "—";
  return new Date(y, m - 1, d).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function FieldRow({ label, last, children }) {
  return (
    <div className={`mmi-row${last ? " mmi-row--last" : ""}`}>
      <span className="mmi-row-label">{label}</span>
      <div className="mmi-row-value">{children}</div>
    </div>
  );
}

export default function MobileTutorMyInfo() {
  const uid = auth.currentUser?.uid;
  const [loading, setLoading] = useState(true);
  const [emp, setEmp] = useState(null);
  const [savedKey, setSavedKey] = useState(null);

  useEffect(() => {
    if (!uid) return;
    (async () => {
      setLoading(true);
      const data = await fetchMyTutorDoc(uid);
      setEmp(data);
      setLoading(false);
    })();
  }, [uid]);

  const flashSaved = (key) => {
    setSavedKey(key);
    setTimeout(() => setSavedKey(null), 1800);
  };

  const saveField = useCallback(
    async (key, value) => {
      if (!uid) return;
      try {
        await updateMyTutorDoc(uid, { [key]: value });
        flashSaved(key);
      } catch (e) {
        console.error("Auto-save failed:", e);
      }
    },
    [uid]
  );

  const setField = (k, v) => setEmp((p) => ({ ...(p || {}), [k]: v }));

  const statusLabel = normalizeStatus(emp?.status);
  const statusKey = statusLabel.toLowerCase().replace(/\s+/g, "-");

  if (loading) {
    return <div className="mmi-loader">Loading…</div>;
  }
  if (!emp) {
    return <div className="mmi-loader mmi-loader--error">Profile not found.</div>;
  }

  return (
    <div className="mmi-container">

      {/* ── Account ── */}
      <p className="mmi-section-label">Account</p>
      <div className="mmi-card">
        <FieldRow label="Full Name">
          <span className="mmi-value-text">{emp.name || "—"}</span>
        </FieldRow>
        <FieldRow label="Email">
          <span className="mmi-value-text mmi-value-text--sm">{emp.email || "—"}</span>
        </FieldRow>
        <FieldRow label="Status" last>
          <span className={`mmi-status mmi-status--${statusKey}`}>{statusLabel}</span>
        </FieldRow>
      </div>

      {/* ── Personal ── */}
      <p className="mmi-section-label">Personal</p>
      <div className="mmi-card">
        <FieldRow label="Phone">
          <div className="mmi-editable">
            <input
              className="mmi-input"
              type="tel"
              value={emp.phone || ""}
              placeholder="Add phone"
              onChange={(e) => setField("phone", e.target.value)}
              onBlur={(e) => saveField("phone", e.target.value)}
            />
            {savedKey === "phone" && <span className="mmi-saved-flash">Saved</span>}
          </div>
        </FieldRow>
        <FieldRow label="Date of Birth" last>
          <div className="mmi-editable">
            <input
              className="mmi-input mmi-input--date"
              type="date"
              value={emp.dob || ""}
              onChange={(e) => setField("dob", e.target.value)}
              onBlur={(e) => saveField("dob", e.target.value)}
            />
            {savedKey === "dob" && <span className="mmi-saved-flash">Saved</span>}
          </div>
        </FieldRow>
      </div>

      {/* ── Employment ── */}
      <p className="mmi-section-label">Employment</p>
      <div className="mmi-card">
        <FieldRow label="Start Date">
          <span className="mmi-value-text">{formatDateDisplay(emp.startWorkingDate)}</span>
        </FieldRow>
        <FieldRow label="Teaching Rate">
          <span className="mmi-value-text">
            {emp.teachingRate != null ? `$${emp.teachingRate}/hr` : "—"}
          </span>
        </FieldRow>
        <FieldRow label="Admin Rate" last>
          <span className="mmi-value-text">
            {emp.adminRate != null ? `$${emp.adminRate}/hr` : "—"}
          </span>
        </FieldRow>
      </div>

      <p className="mmi-footnote">
        Tap a field to edit. Changes to phone and date of birth save automatically.
        Contact admin to update rates or status.
      </p>

    </div>
  );
}
